import * as Sentry from '@sentry/vue'
import { ROUTE_NAMES } from '@/constant/routes.js'
import {
  SEND_EFFECT_ACTION, TOGGLE_QR_CODE,
  TOGGLE_FULL_SCREEN, TOGGLE_INSTRUCTION_BAR,
  TOGGLE_QUIZ_MUSIC, CONTROL_AUDIO,
  NAVIGATE_SLIDE, SET_THEME,
} from '@/vuex/modules/presentation/remote-control-link.module'

const ROUTE_CHANGE_ACTION_TYPE = 'routeChangeAction'
const SPECIAL_EFFECT = 'specialEffect'
const QR_CODE = 'isShowQrcode'
const INSTRUCTION_BAR = 'isShowInstructionBar'
const FULLSCREEN = 'fullscreen'
const QUIZ_SOUND_EFFECT = 'quizSoundEffect'
const AUDIO_CONTROL = 'audioControl'
const NAVIGATE = 'NavigateSlide'
const REMOTE_CONTROL = 'remoteControl'
const THEME_UPDATE = 'update'

// Remote control sync available actions
const TOGGLE_QR_CODE_GLOBAL_ACTION = 'toggleQRCodePanelAction'
const REMOTE_CONTROL_SYNC_AVAILABLE_ACTIONS = [
  TOGGLE_QR_CODE_GLOBAL_ACTION,
]

const _getSyncMethodCreator = (end_parts, type = 'string') => {
  return (store, value) => {
    store.dispatch('kvSync/createKeyValueAction', {
      path: `/REMOTE_CONTROL/${store.state.presentation.id}`,
      end_parts,
      type,
      value: JSON.stringify(value),
    })
  }
}

const syncMethodMap = {
  [TOGGLE_QR_CODE_GLOBAL_ACTION]: _getSyncMethodCreator('isShowQrcode'),
}

const getRemoteControlTopic = (presentationId, topicName) => {
  return `/REMOTE_CONTROL/${presentationId}/${topicName}`
}

const getThemeTopic = (presentationId, action) => {
  return `/PRESENTATION_THEME/${presentationId}/${action}`
}

const getSubscribeKvSyncCreator = (store) => {
  return (topic, callback) => {
    store.dispatch('kvSync/subscribeKeyAction', {
      key: topic,
      callback,
    })
  }
}

const getUnsubscribeKvSyncCreator = (store) => {
  return (topic) => {
    store.dispatch('kvSync/unsubscribeKeyAction', {
      key: topic,
    })
  }
}

const getTopicCreator = (presentationId) => {
  return (topicName) => {
    return getRemoteControlTopic(presentationId, topicName)
  }
}

const getThemeTopicCreator = (presentationId) => {
  return (action) => {
    return getThemeTopic(presentationId, action)
  }
}

const registerKvSyncSubscriber = (store, presentationId) => {
  const subscribeKvSync = getSubscribeKvSyncCreator(store)
  const getTopic = getTopicCreator(presentationId)
  const getThemeTopic = getThemeTopicCreator(presentationId)
  // Consider use this to check if the presentation is presenting before trigger action
  // const { isPresenting } = store.getters

  subscribeKvSync(getTopic(SPECIAL_EFFECT), (_, message) => {
    store.dispatch(SEND_EFFECT_ACTION, {
      effect: message,
      source: REMOTE_CONTROL,
    })
  })

  subscribeKvSync(getTopic(NAVIGATE), (_, message) => {
    store.dispatch(NAVIGATE_SLIDE, {
      source: REMOTE_CONTROL,
      navigate: message,
    })
  })

  subscribeKvSync(getTopic(QR_CODE), (_, message) => {
    store.dispatch(TOGGLE_QR_CODE, {
      value: message === 'true',
      source: REMOTE_CONTROL,
    })
  })

  subscribeKvSync(getTopic(INSTRUCTION_BAR), (_, message) => {
    store.dispatch(TOGGLE_INSTRUCTION_BAR, {
      value: message === 'true',
      source: REMOTE_CONTROL,
    })
  })

  subscribeKvSync(getTopic(FULLSCREEN), (_, message) => {
    store.dispatch(TOGGLE_FULL_SCREEN, {
      value: message === 'true',
      source: REMOTE_CONTROL,
    })
  })

  subscribeKvSync(getTopic(QUIZ_SOUND_EFFECT), (_, message) => {
    store.dispatch(TOGGLE_QUIZ_MUSIC, {
      value: message === 'true',
      source: REMOTE_CONTROL,
    })
  })

  subscribeKvSync(getTopic(AUDIO_CONTROL), (_, message) => {
    try {
      const value = JSON.parse(message)
      store.dispatch(CONTROL_AUDIO, {
        value,
        source: REMOTE_CONTROL,
      })
    } catch (error) {
      Sentry.captureMessage(new Error('Remote Control Audio Control Error'), {
        level: 'warning',
        extra: {
          message,
        },
      })
    }
  })

  subscribeKvSync(getThemeTopic(THEME_UPDATE), (_, message) => {
    try {
      const value = JSON.parse(message) // value = { themeId }
      store.dispatch(SET_THEME, {
        value,
      })
    } catch (error) {
      Sentry.captureMessage(new Error('Remote Control Theme Update Error'), {
        level: 'warning',
        extra: {
          message,
          error,
        },
      })
    }
  })
}

const unregisterKvSyncSubscriber = (store, presentationId) => {
  const unsubscribeKvSync = getUnsubscribeKvSyncCreator(store)
  const getTopic = getTopicCreator(presentationId)
  const getThemeTopic = getThemeTopicCreator(presentationId)

  unsubscribeKvSync(getTopic(SPECIAL_EFFECT))
  unsubscribeKvSync(getTopic(QR_CODE))
  unsubscribeKvSync(getTopic(INSTRUCTION_BAR))
  unsubscribeKvSync(getTopic(FULLSCREEN))
  unsubscribeKvSync(getTopic(QUIZ_SOUND_EFFECT))
  unsubscribeKvSync(getTopic(AUDIO_CONTROL))
  unsubscribeKvSync(getTopic(NAVIGATE))
  unsubscribeKvSync(getThemeTopic(THEME_UPDATE))
}

const triggerRemoteControlSyncHandler = (store, action) => {
  if (!REMOTE_CONTROL_SYNC_AVAILABLE_ACTIONS.includes(action.type)) return
  const syncMethod = syncMethodMap[action.type]
  syncMethod(store, action.payload)
}

const kvSyncSubscriberHandler = (store, action) => {
  if (action.type !== ROUTE_CHANGE_ACTION_TYPE) return
  if (action.payload?.from?.name === ROUTE_NAMES.PRESENTATION_DETAIL &&
      action.payload?.to?.name !== ROUTE_NAMES.PRESENTATION_DETAIL
  ) {
    unregisterKvSyncSubscriber(store, action.payload?.from?.params?.id)
  }

  if (action.payload?.from?.name !== ROUTE_NAMES.PRESENTATION_DETAIL &&
      action.payload?.to?.name === ROUTE_NAMES.PRESENTATION_DETAIL) {
    registerKvSyncSubscriber(store, action.payload?.to?.params?.id)
  }
}

const remoteControlPlugin = (store) => {
  store.subscribeAction((action) => {
    kvSyncSubscriberHandler(store, action)
    triggerRemoteControlSyncHandler(store, action)
  })
}

export default remoteControlPlugin
